/**
 * Menu configuration - single source of truth for sidebar (and future top bar).
 * 8 top-level items: Atendimentos, Gerência, Comunicação, Contatos e base,
 * Campanhas e quadros, Conexões, Administração, Sistema.
 */

import { i18n } from "../translate/i18n";
import menuListItemsAdditions from "../translate/languages/menuListItemsAdditions";

export const buildMenuConfig = ({
  user,
  showCampaigns,
  showKanban,
  showSchedules,
  showInternalChat,
  showOpenAi,
  showIntegrations,
  showExternalApi,
  i18n,
  icons: I,
}) => {
  const canManagement =
    (user.profile === "user" && user.showDashboard === "enabled") ||
    user.profile === "admin";
  const canRealTime =
    (user.profile === "user" && user.allowRealTime === "enabled") ||
    user.profile === "admin";
  const canConnections =
    (user.profile === "user" && user.allowConnections === "enabled") ||
    user.profile === "admin";
  const canAdmin = user.profile === "admin";

  const items = [];

  // 1. Atendimentos (principal - link direto)
  items.push({
    id: "atendimentos",
    labelKey: "mainDrawer.listItems.tickets",
    to: "/tickets",
    icon: I.Whatsapp,
    order: 1,
    canView: true,
  });

  // 2. Gerência (submenu)
  const managementChildren = [];
  if (canManagement) {
    managementChildren.push({
      to: "/",
      labelKey: "mainDrawer.listItems.dashboard",
      icon: I.Dashboard,
    });
    managementChildren.push({
      to: "/reports",
      labelKey: "mainDrawer.listItems.reports",
      icon: I.Notepad2,
    });
  }
  if (canRealTime) {
    managementChildren.push({
      to: "/moments",
      labelKey: "mainDrawer.listItems.chatsTempoReal",
      icon: I.Grid1,
    });
  }
  if (managementChildren.length > 0) {
    items.push({
      id: "management",
      labelKey: "mainDrawer.listItems.management",
      icon: I.HomeTrendUp,
      order: 2,
      children: managementChildren.map((c) => ({
        ...c,
        label: i18n.t(c.labelKey),
      })),
      canView: true,
    });
  }

  // 3. Comunicação (submenu)
  const comunicacaoChildren = [
    { to: "/quick-messages", labelKey: "mainDrawer.listItems.quickMessages", icon: I.Flash },
    { to: "/templates", labelKey: "mainDrawer.listItems.templates", icon: I.Notification1 },
  ];
  if (showInternalChat) {
    comunicacaoChildren.push({
      to: "/chats",
      labelKey: "mainDrawer.listItems.chats",
      icon: I.MessageFavorite,
    });
  }
  comunicacaoChildren.push({
    to: "/helps",
    labelKey: "mainDrawer.listItems.helps",
    icon: I.CalendarEdit,
  });
  items.push({
    id: "comunicacao",
    labelKey: "mainDrawer.listItems.comunicacao",
    icon: I.MessageFavorite,
    order: 3,
    children: comunicacaoChildren.map((c) => ({
      ...c,
      label: i18n.t(c.labelKey),
    })),
    canView: true,
  });

  // 4. Contatos e base (submenu)
  const contatosChildren = [
    { to: "/contacts", labelKey: "mainDrawer.listItems.contacts", icon: I.UserAdd },
    { to: "/tags", labelKey: "mainDrawer.listItems.tags", icon: I.Tag },
  ];
  if (showSchedules) {
    contatosChildren.push({
      to: "/schedules",
      labelKey: "mainDrawer.listItems.schedules",
      icon: I.Calendar,
    });
  }
  items.push({
    id: "contatosEBase",
    labelKey: "mainDrawer.listItems.contatosEBase",
    icon: I.UserAdd,
    order: 4,
    children: contatosChildren.map((c) => ({
      ...c,
      label: i18n.t(c.labelKey),
    })),
    canView: true,
  });

  // 5. Campanhas (submenu - só quando showCampaigns)
  if (showCampaigns) {
    const campanhasChildren = [
      { to: "/campaigns", labelKey: "campaigns.subMenus.list", icon: I.VolumeUp },
      { to: "/contact-lists", labelKey: "campaigns.subMenus.listContacts", icon: I.Profile2User },
      { to: "/campaigns-config", labelKey: "campaigns.subMenus.settings", icon: I.Setting2 },
    ];
    items.push({
      id: "campanhas",
      labelKey: "mainDrawer.listItems.campaigns",
      icon: I.VolumeHigh,
      order: 5,
      children: campanhasChildren.map((c) => ({
        ...c,
        label: i18n.t(c.labelKey),
      })),
      canView: true,
    });
  }

  // 6. Kanban (primeiro nível - submenu: Quadros, Configuração)
  if (showKanban) {
    const kanbanChildren = [
      { to: "/kanban", labelKey: "mainDrawer.listItems.quadros", icon: I.Kanban },
      { to: "/TagsKanban", labelKey: "mainDrawer.listItems.quadrosConfig", icon: I.Kanban },
    ];
    items.push({
      id: "kanban",
      labelKey: "mainDrawer.listItems.kanban",
      icon: I.Kanban,
      order: 6,
      children: kanbanChildren.map((c) => ({
        ...c,
        label: i18n.t(c.labelKey),
      })),
      canView: true,
    });
  }

  // 7. Conexões (link direto)
  items.push({
    id: "connections",
    labelKey: "mainDrawer.listItems.connections",
    to: "/connections",
    icon: I.ArrowSwapHorizontal,
    order: 7,
    canView: canConnections,
  });

  // 8. Inteligência (submenu: Filas e Chatbot, Integrações, API, Open.Ai)
  const inteligenciaChildren = [
    { to: "/queues", labelKey: "mainDrawer.listItems.queues", icon: I.ArrowSquare },
    { to: "/queue-integration", labelKey: "mainDrawer.listItems.queueIntegration", icon: I.Hierarchy },
  ];
  if (showExternalApi) {
    inteligenciaChildren.push({
      to: "/messages-api",
      labelKey: "mainDrawer.listItems.messagesAPI",
      icon: I.CodeCircle,
    });
  }
  if (showOpenAi) {
    inteligenciaChildren.push({
      to: "/prompts",
      labelKey: "mainDrawer.listItems.prompts",
      icon: I.KeyboardOpen,
    });
  }
  items.push({
    id: "inteligencia",
    labelKey: "mainDrawer.listItems.inteligencia",
    icon: I.KeyboardOpen,
    order: 8,
    children: inteligenciaChildren.map((c) => ({
      ...c,
      label: i18n.t(c.labelKey),
    })),
    canView: canAdmin || canConnections,
  });

  // 9. Administração (submenu - sem Filas, Integrações, API, Open.Ai)
  const adminChildren = [
    { to: "/users", labelKey: "mainDrawer.listItems.users", icon: I.People },
    { to: "/plantao", labelKey: "mainDrawer.listItems.orderly", icon: I.Notification1 },
    { to: "/feriados", labelKey: "mainDrawer.listItems.feriados", icon: I.Calendar },
    { to: "/files", labelKey: "mainDrawer.listItems.files", icon: I.Paperclip },
    { to: "/financeiro", labelKey: "mainDrawer.listItems.financeiro", icon: I.MoneySend },
    { to: "/settings", labelKey: "mainDrawer.listItems.settings", icon: I.Setting },
  ];
  items.push({
    id: "administracao",
    labelKey: "mainDrawer.listItems.administracao",
    icon: I.Setting,
    order: 9,
    children: adminChildren.map((c) => ({
      ...c,
      label: i18n.t(c.labelKey),
    })),
    canView: canAdmin || canConnections,
  });

  // 10. Sistema (super only - submenu)
  const sistemaChildren = [];
  if (user.super) {
    sistemaChildren.push(
      { to: "/announcements", labelKey: "mainDrawer.listItems.annoucements", icon: I.Information },
      { to: "/companies", labelKey: "mainDrawer.listItems.companies", icon: I.Buliding }
    );
  }
  if (sistemaChildren.length > 0) {
    items.push({
      id: "sistema",
      labelKey: "mainDrawer.listItems.sistema",
      icon: I.Buliding,
      order: 10,
      children: sistemaChildren.map((c) => ({
        ...c,
        label: i18n.t(c.labelKey),
      })),
      canView: true,
    });
  }

  return items
    .filter((item) => item.canView)
    .sort((a, b) => a.order - b.order);
};

// Register menu category i18n keys so they work without editing language files
function registerMenuListItemsKeys() {
  Object.keys(menuListItemsAdditions).forEach((lng) => {
    const add = menuListItemsAdditions[lng]?.translations?.mainDrawer?.listItems;
    if (add) {
      Object.keys(add).forEach((key) => {
        i18n.addResource(lng, "translations", `mainDrawer.listItems.${key}`, add[key]);
      });
    }

    const extra = menuListItemsAdditions[lng]?.translations?.feriados;
    if (extra) {
      Object.keys(extra).forEach((key) => {
        i18n.addResource(lng, "translations", `feriados.${key}`, extra[key]);
      });
    }
  });
}
registerMenuListItemsKeys();
